import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  overdue: { label: '🔴 متأخر',  color: 'bg-red-100 text-red-800 border-red-300' },
  partial: { label: '🟠 جزئي',   color: 'bg-orange-100 text-orange-800 border-orange-300' },
  pending: { label: '🟡 معلَّق',   color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  paid:    { label: '🟢 مدفوع',  color: 'bg-green-100 text-green-800 border-green-300' },
};

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('ar-DZ');
  } catch (e) {
    return iso;
  }
};

// 🆕 Build a wa.me link from an Algerian phone number. Algerian local
// numbers start with '0' (e.g. 0555123456) and must be sent to wa.me as
// 213555123456 (country code 213, no leading 0). We also strip spaces,
// dashes and parentheses. Returns null when the phone is unusable.
const buildWaLink = (phone, message) => {
  if (!phone) return null;
  // Keep only digits and a leading +
  let digits = String(phone).replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) digits = digits.slice(1);
  // 0XXXXXXXXX → 213XXXXXXXXX
  if (digits.startsWith('0')) digits = '213' + digits.slice(1);
  // Reject if it's still too short to be a valid mobile (Algeria = 12 digits)
  if (digits.length < 8) return null;
  const text = encodeURIComponent(message || '');
  return `https://wa.me/${digits}?text=${text}`;
};

// 🆕 Friendly Arabic reminder template — kept short, polite, and
// auto-filled with the amounts the user already sees on the row.
const buildReminderMessage = (client, agencyName) => {
  const remaining = Number(client.remaining_amount || 0).toLocaleString();
  const overdue = Number(client.overdue_amount || 0).toLocaleString();
  const name = client.client_name || 'عميلنا الكريم';
  const lines = [
    `السلام عليكم ${name} 🌹`,
    '',
    `هذا تذكير ودّي من ${agencyName || 'وكالة صنهاجة للسفر'} بخصوص حساب الأقساط لديكم:`,
    `• إجمالي المتبقي: ${remaining} دج`,
  ];
  if (client.overdue_amount > 0) {
    lines.push(`• المبلغ المتأخر: ${overdue} دج (${client.overdue_count} قسط)`);
  }
  if (client.next_due_date) {
    try {
      const d = new Date(client.next_due_date).toLocaleDateString('ar-DZ');
      lines.push(`• تاريخ القسط القادم: ${d}`);
    } catch (e) { /* ignore */ }
  }
  lines.push('');
  lines.push('نرجو منكم التكرّم بسداد المستحقّات في أقرب وقت ممكن، أو التواصل معنا لتحديد موعد. شكراً لتعاونكم 🙏');
  return lines.join('\n');
};

export default function InstallmentsByAgencyOverview({ user }) {
  const [data, setData] = useState({ agencies: [], grand_totals: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // { agency_id: true }

  // Filters
  const [filters, setFilters] = useState({
    agency_id: 'all',
    start_date: '',
    end_date: '',
    status: 'all',
    client_search: '',
  });
  const [agencies, setAgencies] = useState([]); // for super_admin filter

  const isSuperAdmin = user?.role === 'super_admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (isSuperAdmin && filters.agency_id && filters.agency_id !== 'all') {
        params.append('agency_id', filters.agency_id);
      }
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.client_search) params.append('client_search', filters.client_search.trim());

      const res = await axios.get(`${API}/installment-reports/by-agency?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || { agencies: [], grand_totals: null });
    } catch (e) {
      console.error('installments-by-agency failed:', e);
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, isSuperAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/agencies`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAgencies(r.data || []))
      .catch(() => {});
  }, [isSuperAdmin]);

  const grand = data.grand_totals || {
    clients_count: 0, receipts_count: 0, total_amount: 0, paid_amount: 0,
    remaining_amount: 0, overdue_amount: 0, overdue_count: 0,
  };

  const collectionRate = useMemo(() => {
    const t = grand.total_amount || 0;
    if (!t) return 0;
    return Math.round((grand.paid_amount / t) * 100);
  }, [grand]);

  const resetFilters = () => setFilters({
    agency_id: 'all', start_date: '', end_date: '', status: 'all', client_search: '',
  });

  const toggleExpand = (agencyId) => {
    setExpanded(prev => ({ ...prev, [agencyId]: !prev[agencyId] }));
  };

  return (
    <div className="p-6 space-y-6" data-testid="installments-by-agency-page" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📊 الأقساط حسب الوكالة</h1>
          <p className="text-sm text-gray-600 mt-1">
            نظرة شاملة على كل الوكالات وحالة عملائها — تحصيل، متأخرات، مواعيد قادمة
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" data-testid="refresh-btn">🔄 تحديث</Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="kpis-row">
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-xs text-blue-800">العملاء</div>
            <div className="text-2xl font-bold text-blue-900" data-testid="kpi-clients">{fmt(grand.clients_count)}</div>
          </CardContent>
        </Card>
        <Card className="border-2 border-indigo-200 bg-indigo-50">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-xs text-indigo-800">الوصولات</div>
            <div className="text-2xl font-bold text-indigo-900" data-testid="kpi-receipts">{fmt(grand.receipts_count)}</div>
          </CardContent>
        </Card>
        <Card className="border-2 border-gray-200 bg-gray-50">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-xs text-gray-700">الإجمالي</div>
            <div className="text-xl font-bold text-gray-900" data-testid="kpi-total">{fmt(grand.total_amount)} دج</div>
          </CardContent>
        </Card>
        <Card className="border-2 border-green-200 bg-green-50">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-xs text-green-800">المُحصَّل</div>
            <div className="text-xl font-bold text-green-900" data-testid="kpi-paid">{fmt(grand.paid_amount)} دج</div>
            <div className="text-[10px] text-green-700 mt-0.5">{collectionRate}% من الإجمالي</div>
          </CardContent>
        </Card>
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-xs text-amber-800">المتبقي</div>
            <div className="text-xl font-bold text-amber-900" data-testid="kpi-remaining">{fmt(grand.remaining_amount)} دج</div>
          </CardContent>
        </Card>
        <Card className="border-2 border-red-300 bg-red-50">
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-xs text-red-800">المتأخر</div>
            <div className="text-xl font-bold text-red-900" data-testid="kpi-overdue">{fmt(grand.overdue_amount)} دج</div>
            <div className="text-[10px] text-red-700 mt-0.5">{grand.overdue_count} قسط</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">🔍 الفلاتر</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {isSuperAdmin && (
              <div>
                <Label className="text-xs">الوكالة</Label>
                <select
                  value={filters.agency_id}
                  onChange={(e) => setFilters({ ...filters, agency_id: e.target.value })}
                  className="w-full p-2 border rounded text-sm"
                  data-testid="filter-agency"
                >
                  <option value="all">كل الوكالات</option>
                  {agencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label className="text-xs">من تاريخ</Label>
              <Input type="date" value={filters.start_date}
                     onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                     data-testid="filter-start-date" />
            </div>
            <div>
              <Label className="text-xs">إلى تاريخ</Label>
              <Input type="date" value={filters.end_date}
                     onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                     data-testid="filter-end-date" />
            </div>
            <div>
              <Label className="text-xs">الحالة</Label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full p-2 border rounded text-sm"
                data-testid="filter-status"
              >
                <option value="all">كل الحالات</option>
                <option value="overdue">🔴 متأخر</option>
                <option value="partial">🟠 جزئي</option>
                <option value="pending">🟡 معلَّق</option>
                <option value="paid">🟢 مدفوع</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">بحث (اسم/هاتف)</Label>
              <Input value={filters.client_search} placeholder="اسم العميل أو رقمه"
                     onChange={(e) => setFilters({ ...filters, client_search: e.target.value })}
                     data-testid="filter-search" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={resetFilters} variant="outline" size="sm" data-testid="reset-filters-btn">
              ↺ إعادة تعيين الفلاتر
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Agencies list */}
      {loading ? (
        <div className="text-center py-12 text-gray-500" data-testid="loading-state">⏳ جاري التحميل...</div>
      ) : error ? (
        <div className="text-center py-12 text-red-600" data-testid="error-state">❌ {error}</div>
      ) : data.agencies.length === 0 ? (
        <div className="text-center py-12 text-gray-500" data-testid="empty-state">
          لا توجد بيانات أقساط تطابق الفلاتر المحدَّدة.
        </div>
      ) : (
        <div className="space-y-3" data-testid="agencies-list">
          {data.agencies.map(ag => {
            const t = ag.totals || {};
            const isOpen = !!expanded[ag.agency_id];
            const rate = t.total_amount ? Math.round((t.paid_amount / t.total_amount) * 100) : 0;
            return (
              <Card
                key={ag.agency_id}
                className={`border-2 ${t.overdue_count > 0 ? 'border-red-300' : 'border-gray-200'}`}
                data-testid={`agency-card-${ag.agency_id}`}
              >
                <CardHeader
                  className="pb-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleExpand(ag.agency_id)}
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{isOpen ? '▼' : '◀'}</span>
                      <div>
                        <CardTitle className="text-lg" data-testid={`agency-name-${ag.agency_id}`}>
                          🏢 {ag.agency_name}
                        </CardTitle>
                        <div className="text-xs text-gray-600 mt-0.5">
                          {t.clients_count} عميل • {t.receipts_count} وصل
                          {t.overdue_count > 0 && (
                            <span className="text-red-700 font-bold ms-2">
                              • {t.overdue_count} قسط متأخر ⚠️
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="bg-gray-50 px-3 py-1.5 rounded">
                        <div className="text-gray-600">الإجمالي</div>
                        <div className="font-bold">{fmt(t.total_amount)} دج</div>
                      </div>
                      <div className="bg-green-50 px-3 py-1.5 rounded">
                        <div className="text-green-700">المُحصَّل ({rate}%)</div>
                        <div className="font-bold text-green-800">{fmt(t.paid_amount)} دج</div>
                      </div>
                      <div className="bg-amber-50 px-3 py-1.5 rounded">
                        <div className="text-amber-700">المتبقي</div>
                        <div className="font-bold text-amber-800">{fmt(t.remaining_amount)} دج</div>
                      </div>
                      <div className={`px-3 py-1.5 rounded ${t.overdue_count > 0 ? 'bg-red-100' : 'bg-gray-50'}`}>
                        <div className={t.overdue_count > 0 ? 'text-red-700' : 'text-gray-600'}>المتأخر</div>
                        <div className={`font-bold ${t.overdue_count > 0 ? 'text-red-800' : 'text-gray-700'}`}>
                          {fmt(t.overdue_amount)} دج
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-3 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-green-500 h-2 transition-all"
                      style={{ width: `${rate}%` }}
                      title={`${rate}% مُحصَّل`}
                    />
                  </div>
                </CardHeader>

                {isOpen && (
                  <CardContent className="pt-0">
                    <div className="overflow-x-auto border rounded">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="p-2 text-right">العميل</th>
                            <th className="p-2 text-right">الحالة</th>
                            <th className="p-2 text-right">الوصولات</th>
                            <th className="p-2 text-right">الإجمالي</th>
                            <th className="p-2 text-right">المدفوع</th>
                            <th className="p-2 text-right">المتبقي</th>
                            <th className="p-2 text-right">المتأخر</th>
                            <th className="p-2 text-right">تاريخ القسط القادم</th>
                            <th className="p-2 text-right">إجراء</th>
                          </tr>
                        </thead>
                        <tbody data-testid={`agency-clients-tbody-${ag.agency_id}`}>
                          {ag.clients.map((c) => {
                            const meta = STATUS_META[c.status] || STATUS_META.pending;
                            const waLink = c.remaining_amount > 0
                              ? buildWaLink(c.client_phone, buildReminderMessage(c, ag.agency_name))
                              : null;
                            return (
                              <tr
                                key={c.client_id || c.client_name}
                                className={`border-t ${c.status === 'overdue' ? 'bg-red-50/40' : ''}`}
                                data-testid={`client-row-${c.client_id || c.client_name}`}
                              >
                                <td className="p-2 font-semibold">
                                  {c.client_name}
                                  {c.client_phone && (
                                    <div className="text-[10px] text-gray-500">📞 {c.client_phone}</div>
                                  )}
                                </td>
                                <td className="p-2">
                                  <Badge className={`${meta.color} border font-bold`} variant="outline">{meta.label}</Badge>
                                </td>
                                <td className="p-2">{c.receipts_count}</td>
                                <td className="p-2 font-bold">{fmt(c.total_amount)} دج</td>
                                <td className="p-2 text-green-700">{fmt(c.paid_amount)} دج</td>
                                <td className="p-2 text-amber-700">{fmt(c.remaining_amount)} دج</td>
                                <td className={`p-2 font-bold ${c.overdue_amount > 0 ? 'text-red-700' : 'text-gray-400'}`}>
                                  {c.overdue_amount > 0 ? `${fmt(c.overdue_amount)} دج (${c.overdue_count})` : '—'}
                                </td>
                                <td className="p-2">
                                  {c.next_due_date ? (
                                    <span className={new Date(c.next_due_date) < new Date() ? 'text-red-700 font-bold' : 'text-gray-700'}>
                                      {formatDate(c.next_due_date)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {waLink ? (
                                    <a
                                      href={waLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-[11px] font-bold whitespace-nowrap shadow-sm transition"
                                      title={`إرسال تذكير عبر WhatsApp لـ ${c.client_name}`}
                                      data-testid={`wa-reminder-${c.client_id || c.client_name}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      📲 تذكير
                                    </a>
                                  ) : c.remaining_amount > 0 && !c.client_phone ? (
                                    <span className="text-[10px] text-gray-400" title="لا يوجد رقم هاتف للعميل">📵 بلا هاتف</span>
                                  ) : (
                                    <span className="text-[10px] text-gray-300">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
